import {
  IoExpandOutline,
  IoAlbumsOutline,
  IoNewspaperOutline,
  IoMegaphoneOutline,
  IoImageOutline,
  IoBookOutline,
  IoGridOutline,
  IoEllipseOutline,
  IoTrophyOutline,
  IoPlayCircleOutline,
  IoTimeOutline,
  IoAppsOutline,
  IoStarOutline,
  IoFlashOutline,
  IoLayersOutline,
  IoHeartOutline,
  IoRibbonOutline,
  IoRadioOutline,
  IoCalendarOutline,
} from "react-icons/io5";
import { TbSparkles } from "react-icons/tb";

// The Widgets Library shown in the Experience Builder's left panel is
// backend-driven (GET /api/v1/widget-types) instead of hardcoded here -- see
// HomePageBuilder's fetchWidgetTypes. `value` is what's persisted as
// SectionCategory.layout_type on the backend (a free-form string, no
// schema-level allow-list), so any row the API returns is immediately
// usable, as long as RAIL_CLASS_KEY (below) and the canvas renderer know how
// to preview it -- unknown values fall back to the default rail renderer.

// Icons are stored on the backend as react-icons/io5 export names (strings)
// since JSX can't be persisted to a database; this maps those names back to
// elements for rendering.
export const WIDGET_ICON_MAP = {
  IoExpandOutline: <IoExpandOutline />,
  IoAlbumsOutline: <IoAlbumsOutline />,
  IoNewspaperOutline: <IoNewspaperOutline />,
  IoMegaphoneOutline: <IoMegaphoneOutline />,
  IoImageOutline: <IoImageOutline />,
  IoBookOutline: <IoBookOutline />,
  IoGridOutline: <IoGridOutline />,
  IoEllipseOutline: <IoEllipseOutline />,
  IoTrophyOutline: <IoTrophyOutline />,
  IoPlayCircleOutline: <IoPlayCircleOutline />,
  IoTimeOutline: <IoTimeOutline />,
  IoAppsOutline: <IoAppsOutline />,
  IoStarOutline: <IoStarOutline />,
  IoFlashOutline: <IoFlashOutline />,
  IoLayersOutline: <IoLayersOutline />,
  IoHeartOutline: <IoHeartOutline />,
  IoRibbonOutline: <IoRibbonOutline />,
  IoRadioOutline: <IoRadioOutline />,
  IoCalendarOutline: <IoCalendarOutline />,
};

export const DEFAULT_WIDGET_ICON = "IoAppsOutline";

// Groups a flat list of widget rows (as returned by GET /api/v1/widget-types,
// already ordered by sort_order) into the { heading, items } shape the
// Widgets Library renders, preserving the order headings first appear in.
export function groupWidgetRows(rows) {
  const groups = [];
  const byHeading = new Map();
  for (const row of rows || []) {
    const heading = row.heading || "CUSTOM";
    let group = byHeading.get(heading);
    if (!group) {
      group = { heading, items: [] };
      byHeading.set(heading, group);
      groups.push(group);
    }
    group.items.push({
      id: row.id,
      value: row.value,
      label: row.label,
      description: row.description,
      icon: WIDGET_ICON_MAP[row.icon] || WIDGET_ICON_MAP[DEFAULT_WIDGET_ICON],
    });
  }
  return groups;
}

// Widgets whose canvas preview is a single large banner rather than a rail of
// cards -- everything else falls through to the default card-rail renderer.
// NOTE: duplicated in the backend as Client/SectionController.js's
// CLIENT_CAROUSEL_LAYOUT_TYPES -- keep both lists in sync, they will
// silently drift otherwise.
// "carousel" is the Featured Carousel widget -- the website already renders
// it with the same Banner component as the hero (Home_page/home.js).
export const BANNER_LAYOUT_TYPES = ["carousel", "hero-banner", "editorial-collection", "promotional-banner"];

// A page with a Grid may only also hold the banner widgets above; pages
// without a Grid are unrestricted. Gives instant feedback in the builder; the
// real rule is enforced by the backend (SectionController.js's
// gridLayoutConflict, same messages) -- keep both in sync.
export const GRID_LAYOUT_TYPE = "grid";
const isBannerOrGrid = (layoutType) =>
  layoutType === GRID_LAYOUT_TYPE || BANNER_LAYOUT_TYPES.includes(layoutType);
export function gridLayoutConflict(existingLayoutTypes, layoutType) {
  if (layoutType === GRID_LAYOUT_TYPE && existingLayoutTypes.some((type) => !isBannerOrGrid(type))) {
    return "Grid can only be added alongside banner layouts (Hero Banner, Featured Carousel, Editorial Collection, Promotional Banner). Remove the other layouts first.";
  }
  if (!isBannerOrGrid(layoutType) && existingLayoutTypes.includes(GRID_LAYOUT_TYPE)) {
    return "In order to add any other layout, remove the Grid layout first.";
  }
  return null;
}

// Widgets whose content is computed server-side rather than admin-picked.
// NOTE: duplicated in the backend as SectionController.js's
// ALGORITHMIC_DATA_SOURCES -- keep both in sync, they will silently drift
// otherwise.
export const ALGORITHMIC_DATA_SOURCES = [
  "trending",
  "continue-watching",
  "recently-added",
  "coming-soon",
  "watch-again",
  "your-next-watch",
  "because-you-watched",
];

// Smart rows: the five computed home-page shelves. The builder can reorder
// them and hide them, nothing else -- no delete, no rename, no hand-picked
// content. Their cards come from the same place they always did
// (HomeSectionsService); the builder only decides position and visibility.
// NOTE: mirrored in the backend as utils/smartRows.js's SMART_ROW_DEFS --
// keep both in sync, they will silently drift otherwise.
// The array order is the order the backend provisions them in, which is the
// order the apps have always rendered them in -- keep the two identical.
// `label` is what the builder calls the row regardless of what the admin has
// renamed it to. Nothing here is matched against the backend -- a smart row is
// identified by its key alone (config.smartRow), so this copy can be reworded
// freely without touching behaviour.
export const SMART_ROW_DEFS = [
  {
    key: "recently-added",
    layoutType: "row-16x9",
    label: "Recently Added",
    description: "Newest content first.",
  },
  {
    key: "coming-soon",
    layoutType: "row-2x3",
    label: "Coming Soon",
    description: "Published titles that aren't playable yet, with a trailer.",
  },
  {
    key: "your-next-watch",
    layoutType: "row-2x3",
    label: "Your Next Watch",
    description:
      "Unwatched titles ranked by what the viewer has been watching.",
  },
  {
    key: "because-you-watched",
    layoutType: "row-2x3",
    label: "Because You Watched",
    description:
      "Titles closest in meaning to the last thing the viewer watched. Its heading names that title for each viewer — rename the row and your heading is used instead, the same for everyone.",
  },
  {
    key: "watch-again",
    layoutType: "row-2x3",
    label: "Watch Again",
    description:
      "Titles the viewer finished, minus anything they're still part-way through.",
  },
];

const SMART_ROW_BY_KEY = new Map(SMART_ROW_DEFS.map((def) => [def.key, def]));

// The smart row key a category is, or null for an ordinary one. Read from
// config.smartRow, never from dataSource alone: an admin's own Recently Added
// rail carries that data source too, and stays fully editable.
export function smartRowKey(category) {
  const key = category?.config?.smartRow;
  return key && SMART_ROW_BY_KEY.has(key) ? key : null;
}

export function isSmartRow(category) {
  return smartRowKey(category) !== null;
}

// Is this one of the five the system provisions onto the home page, as
// opposed to a copy the admin added from the Modules panel?
//
// Asked as "is it NOT marked admin-added", never "does it carry
// provisionedName": rows minted before that field existed have none, so
// keying on its presence classed every long-standing home row as
// admin-added and offered a Delete that the next builder load silently
// undid. Mirrors the backend's isProvisionedSmartRow (utils/smartRows.js).
export function isProvisionedSmartRow(category) {
  return isSmartRow(category) && category?.config?.addedByAdmin !== true;
}

export function isSmartRowHidden(category) {
  return isSmartRow(category) && category?.config?.hidden === true;
}

export function smartRowDef(category) {
  return SMART_ROW_BY_KEY.get(smartRowKey(category)) || null;
}

// Builder modules: sections the builder can place but whose CONTENT it never
// touches. They live in their own rail tab (Modules), NOT in the Widgets
// Library, because there is no data source to pick and no media to add --
// the cards come from elsewhere in the CMS.
//
// What the builder may do with one: drop it on ANY page, reorder it, rename
// its heading, hide it, delete it. What it may not do: add, remove or reorder
// its cards. Unlike a smart row, a module is never auto-provisioned -- it
// exists only where an admin put it.
//
// NOTE: mirrored in the backend as utils/builderModules.js's
// BUILDER_MODULE_DEFS -- keep both in sync, they will silently drift
// otherwise.
export const BUILDER_MODULE_DEFS = [
  {
    key: "live-tv",
    label: "Live-TV",
    description: "Your active channels, with what's on now.",
    // Why this module exists: an Active channel is NOT public by itself.
    // Channels reach the apps only once this row is on a published page.
    hint: "Channels stay hidden from the apps until this section is published.",
    // The sparkle, not a broadcast mark: Live-TV behaves exactly like a smart
    // row (placed anywhere, reordered, renamed, hidden, never hand-filled),
    // so showing it as a different kind of thing only invited the question of
    // how it differs. It doesn't.
    icon: <TbSparkles />,
  },
];

const BUILDER_MODULE_BY_KEY = new Map(
  BUILDER_MODULE_DEFS.map((def) => [def.key, def]),
);

// Everything the Modules panel can ADD to the page, in the order it lists
// them. Two kinds, deliberately side by side: a module reads its cards from
// elsewhere in the CMS (Live-TV -> the Channels page), a smart row computes
// its own per viewer. The builder treats them identically -- place, reorder,
// rename, hide, delete -- which is why they share a panel.
//
// `kind` is what createCategory keys off to decide which config to stamp.
export const ADDABLE_MODULES = [
  ...BUILDER_MODULE_DEFS.map((def) => ({
    kind: "module",
    key: def.key,
    layoutType: def.key,
    label: def.label,
    description: def.description,
    icon: def.icon,
  })),
  ...SMART_ROW_DEFS.map((def) => ({
    kind: "smart-row",
    key: def.key,
    layoutType: def.layoutType,
    label: def.label,
    description: def.description,
    icon: <TbSparkles />,
  })),
];

// The module key a category is, or null for an ordinary one. Read from
// config.module first (what the Modules panel writes), falling back to
// layout_type so a row created before config.module existed still locks.
export function builderModuleKey(category) {
  const fromConfig = category?.config?.module;
  if (fromConfig && BUILDER_MODULE_BY_KEY.has(fromConfig)) return fromConfig;
  const fromLayout = category?.layout_type;
  return fromLayout && BUILDER_MODULE_BY_KEY.has(fromLayout) ? fromLayout : null;
}

export function isBuilderModule(category) {
  return builderModuleKey(category) !== null;
}

export function isBuilderModuleHidden(category) {
  return isBuilderModule(category) && category?.config?.hidden === true;
}

export function builderModuleDef(category) {
  return BUILDER_MODULE_BY_KEY.get(builderModuleKey(category)) || null;
}

// True for a layout type that is a module, for the paths that only have the
// layout string in hand (createCategory, the canvas drop handler).
export function isBuilderModuleLayout(layoutType) {
  return BUILDER_MODULE_BY_KEY.has(layoutType);
}

export const RAIL_CLASS_KEY = {
  "live-tv": "railRow16x9",
  carousel: "railCarousel",
  circle: "railCircle",
  square: "railSquare",
  "row-16x9": "railRow16x9",
  grid: "railGrid16x9",
  "row-2x3": "railRow2x3",
  top10: "railRow2x3",
  "continue-watching": "railRow16x9",
  "recently-added": "railRow16x9",
  "live-channels": "railRow16x9",
  "upcoming-events": "railRow16x9",
};

// Suggests each layout type's natural card shape -- a Circle Avatars row as
// circles, a Square Grid as squares, etc. The Section Inspector's "Image
// ratio" control (cardDesign.imageRatio) always takes priority when set; this
// map is only the fallback for categories saved before that field existed.
export const LAYOUT_ASPECT_RATIO = {
  "live-tv": "16:9",
  carousel: "16:9",
  "row-16x9": "16:9",
  grid: "16:9",
  "continue-watching": "16:9",
  "recently-added": "16:9",
  "live-channels": "16:9",
  "upcoming-events": "16:9",
  "row-2x3": "2:3",
  top10: "2:3",
  square: "1:1",
  circle: "1:1",
};

export const DEVICE_PRESETS = {
  web: { label: "Web", frameWidth: null, caption: "Web · Responsive", navStyle: "top" },
  tv: { label: "TV", frameWidth: 960, caption: "TV · 1920 × 1080 · 10-foot UI · top nav", navStyle: "tv" },
  mobile: { label: "Mobile", frameWidth: 340, caption: "Mobile · 390 × 844 · bottom tab nav", navStyle: "bottom" },
};

// "manual" is hand-picked content only, added via Browse Media Library.
// "category" pulls its content from the category/genre tags picked in the
// "Add Content via Category" row below the Data Source select -- unlike the
// algorithmic sources it's still admin-driven, not server-computed, so it's
// deliberately left out of ALGORITHMIC_DATA_SOURCES below.
export const DATA_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual Content" },
  { value: "category", label: "Category" },
  { value: "playlist", label: "Playlist" },
  { value: "tags", label: "Tags" },
  { value: "search-filter", label: "Search Filter" },
  { value: "recommendation-engine", label: "Recommendation Engine" },
  { value: "recently-added", label: "Recently Added" },
  { value: "trending", label: "Trending" },
  { value: "most-watched", label: "Most Watched" },
  { value: "ai-collection", label: "AI Collection" },
  { value: "continue-watching", label: "Continue Watching" },
];

// Listed in DATA_SOURCE_OPTIONS above so the picker matches the full design
// spec, but nothing server-side computes their content yet -- picking one
// just falls back to manual/Browse-Media-Library behavior with a note
// telling the admin it isn't wired up yet. Remove an entry here once its
// real fetch logic lands (and add it to ALGORITHMIC_DATA_SOURCES if it ends
// up server-computed).
export const PLACEHOLDER_DATA_SOURCES = [
  "playlist",
  "tags",
  "search-filter",
  "recommendation-engine",
  "most-watched",
  "ai-collection",
];

// No 4:3 here on purpose: the website has three card shapes -- landscape,
// square and portrait (Standard/style.js) -- and no 4:3 card to render one
// with, so SHAPE_BY_IMAGE_RATIO maps a stored "4:3" onto the landscape card.
// Offering it meant an admin picked "4:3 Classic", watched the canvas preview
// 4:3 cards, published, and got 16:9 on the site. A row saved with "4:3"
// before this still previews and renders as landscape, and shows 16:9 here
// (see getEffectiveImageRatio).
export const IMAGE_RATIO_OPTIONS = [
  { value: "16:9", label: "16:9 Landscape" },
  { value: "2:3", label: "2:3 Portrait" },
  { value: "1:1", label: "1:1 Square" },
];
export const TITLE_POSITION_OPTIONS = [
  { value: "below", label: "Below card" },
  { value: "overlay", label: "Overlay" },
  { value: "hidden", label: "Hidden" },
];

export const THEME_ACCENT_PRESETS = ["#B87333", "#E24B4A", "#3E7BFA", "#2FA86A", "#8B5CF6", "#E8B93A"];

// Primary text color for the app (rail titles, card names, active nav).
export const THEME_FONT_COLOR_PRESETS = ["#FFFFFF", "#E5E5E5", "#B1B1B1", "#F5E6D3", "#111111"];

// Extended palette shown in the "+" popover when adding a custom accent
// color or a custom app background. The fixed preset lists above stay the
// quick-pick sets; anything chosen from here (or typed as a hex value) is
// saved server-side (ThemeSetting.custom_accent_colors /
// .custom_backgrounds), so it persists and shows up as an extra swatch from
// then on.
export const THEME_COLOR_PALETTE = [
  "#F87171", "#EF4444", "#DC2626", "#F97316", "#EA580C", "#F59E0B",
  "#EAB308", "#FACC15", "#84CC16", "#65A30D", "#22C55E", "#16A34A",
  "#10B981", "#14B8A6", "#06B6D4", "#0EA5E9", "#3B82F6", "#2563EB",
  "#6366F1", "#7C3AED", "#A855F7", "#C026D3", "#D946EF", "#EC4899",
  "#F43F5E", "#78716C", "#64748B", "#334155", "#111827", "#FFFFFF",
];
export const THEME_BACKGROUND_PRESETS = [
  { value: "charcoal", label: "Charcoal", color: "#141414" },
  { value: "midnight", label: "Midnight", color: "#0D1220" },
  { value: "wine", label: "Wine", color: "#210F14" },
  { value: "forest", label: "Forest", color: "#0F1D14" },
];
export const THEME_FOCUS_EFFECTS = [
  { value: "glow-ring", label: "Glow Ring" },
  { value: "scale", label: "Scale" },
  { value: "lift", label: "Lift" },
];
export const THEME_FONT_OPTIONS = ["Poppins", "Inter", "Roboto", "Montserrat"];
